import { z } from 'zod';

export const uuidSchema = z.uuid();

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/**
 * UUID version 7 (RFC 9562): 48-bit Unix time in milliseconds + random bits.
 * Rows created on the phone use it, so ids sort roughly by creation time.
 * `randomBytes` is injected because each runtime has its own secure random source
 * (expo-crypto on the phone, `crypto.getRandomValues` elsewhere).
 */
export function uuidV7(
  randomBytes: (length: number) => Uint8Array,
  now: number = Date.now(),
): string {
  if (!Number.isSafeInteger(now) || now < 0 || now > 0xffff_ffff_ffff) {
    throw new RangeError('uuidV7: timestamp out of range');
  }
  const random = randomBytes(10);
  if (random.length < 10) {
    throw new RangeError('uuidV7: randomBytes returned too few bytes');
  }
  const bytes = new Uint8Array(16);
  let time = now;
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = time % 256;
    time = Math.floor(time / 256);
  }
  bytes.set(random.subarray(0, 10), 6);
  bytes[6] = 0x70 | ((bytes[6] as number) & 0x0f); // version 7
  bytes[8] = 0x80 | ((bytes[8] as number) & 0x3f); // RFC 9562 variant
  let hex = '';
  for (const byte of bytes) {
    hex += HEX[byte];
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Milliseconds since the Unix epoch encoded in a UUID v7. */
export function uuidV7Timestamp(uuid: string): number {
  return Number.parseInt(uuid.replace(/-/g, '').slice(0, 12), 16);
}
